import { useState, useEffect } from 'react'
import { ChevronDown, ChevronUp, X, AlertTriangle } from 'lucide-react'

const STORAGE_KEY = 'cor_disclaimer_dismissed_v1'

export default function DisclaimerBanner() {
  const [dismissed, setDismissed] = useState(false)
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY) === 'true') {
      setDismissed(true)
    }
  }, [])

  if (dismissed) return null

  return (
    <div className="bg-amber-50 border-b border-amber-200 text-amber-900 text-sm">
      {/* Collapsed bar */}
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-2.5 flex items-center gap-3">
        <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
        <span className="font-semibold text-amber-800 shrink-0">Pilot Phase &amp; Development Disclaimer</span>
        <span className="text-amber-700 text-xs hidden sm:inline truncate">
          This platform is currently in a pilot/beta stage. Features, AI responses, and data may be incomplete or subject to change.
        </span>
        <div className="ml-auto flex items-center gap-2 shrink-0">
          <button
            onClick={() => setExpanded(e => !e)}
            className="flex items-center gap-1 px-2.5 py-1 rounded-md border border-amber-300 bg-white text-amber-800 text-xs font-medium hover:bg-amber-100 transition-colors"
            aria-expanded={expanded}
          >
            {expanded ? (
              <><ChevronUp className="w-3.5 h-3.5" /> Collapse</>
            ) : (
              <><ChevronDown className="w-3.5 h-3.5" /> Read full disclaimer</>
            )}
          </button>
          <button
            onClick={() => {
              sessionStorage.setItem(STORAGE_KEY, 'true')
              setDismissed(true)
            }}
            className="p-1 rounded-md text-amber-600 hover:bg-amber-200 transition-colors"
            title="Dismiss for this session"
            aria-label="Dismiss disclaimer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Expanded full text */}
      {expanded && (
        <div className="border-t border-amber-200 bg-amber-50/80">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-5 space-y-4 text-xs text-amber-900 leading-relaxed">
            <p>
              This platform, including all portals, dashboards, AI-powered tools, and related digital services provided by{' '}
              <strong>Club of Royals LLC (Minnesota)</strong> under the OM Open ecosystem, is currently in a pilot, beta, or
              pre-release development stage.
            </p>

            <div>
              <p className="font-semibold mb-1">During this phase:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Features may be added, removed, or modified without prior notice.</li>
                <li>System behavior, AI responses, and data outputs may be incomplete, inaccurate, or subject to change.</li>
                <li>Performance, availability, and reliability may vary.</li>
                <li>Integrations, APIs, and automation workflows may not yet be fully validated.</li>
              </ul>
            </div>

            <div>
              <p className="font-semibold mb-1">No Warranties</p>
              <p>
                All services are provided on an &ldquo;as-is,&rdquo; &ldquo;as-available,&rdquo; and &ldquo;with all faults&rdquo; basis. Club of Royals LLC
                expressly disclaims all warranties, whether express or implied, including but not limited to merchantability,
                fitness for a particular purpose, accuracy, and non-infringement.
              </p>
            </div>

            <div>
              <p className="font-semibold mb-1">Limitation of Liability</p>
              <p>
                To the fullest extent permitted under Minnesota law, Club of Royals LLC shall not be liable for any damages
                arising from the use or inability to use the pilot-phase platform, including but not limited to data loss,
                inaccuracies, service interruptions, or reliance on system outputs.
              </p>
            </div>

            <div>
              <p className="font-semibold mb-1">User Acknowledgment</p>
              <p>
                By accessing or using this platform, you acknowledge that it is under development and agree to use it solely
                for evaluation, testing, or non-critical purposes until the official production release.
              </p>
            </div>

            <div className="flex justify-end pt-1">
              <button
                onClick={() => {
                  sessionStorage.setItem(STORAGE_KEY, 'true')
                  setDismissed(true)
                }}
                className="px-4 py-1.5 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-xs font-semibold transition-colors"
              >
                I understand, dismiss
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
